import React, { useEffect, useState, useRef } from "react";

const quotes = [
  { text: "Even the best code throws an error sometimes.", emoji: "💻" },
  { text: "Don't worry — even Google goes down sometimes!", emoji: "🌐" },
  { text: "Errors are just plot twists in the story of success.", emoji: "✨" },
  { text: "Take a deep breath, drink water, and try again.", emoji: "☕" },
  { text: "Failure is not fatal — unless it's a missing semicolon.", emoji: "😉" },
  { text: "Who needs perfection when you have persistence?", emoji: "💪" },
  { text: "Relax. The code is testing your patience.", emoji: "🧘" }
];

const ErrorPage = () => {
  const [quote, setQuote] = useState(quotes[0]);
  const [showGame, setShowGame] = useState(false);
  
  // Game states
  const [isGameRunning, setIsGameRunning] = useState(false);
  const [score, setScore] = useState(0);
  const [highScore, setHighScore] = useState(0);
  const [playerY, setPlayerY] = useState(0);
  const [isJumping, setIsJumping] = useState(false);
  const [obstacles, setObstacles] = useState([]);
  const [gameOver, setGameOver] = useState(false);
  const [gameSpeed, setGameSpeed] = useState(6);

  const gameLoopRef = useRef(null);
  const obstacleIntervalRef = useRef(null);

  useEffect(() => {
    const randomQuote = quotes[Math.floor(Math.random() * quotes.length)];
    setQuote(randomQuote);
  }, []);

  // Jump function with improved physics
  const jump = () => {
    if (!isJumping && isGameRunning && !gameOver) {
      setIsJumping(true);
      let jumpHeight = 0;
      let velocity = 12;
      const jumpInterval = setInterval(() => {
        velocity -= 0.8;
        jumpHeight += velocity;
        setPlayerY(Math.max(0, jumpHeight));
        
        if (jumpHeight <= 0) {
          clearInterval(jumpInterval);
          setPlayerY(0);
          setIsJumping(false);
        }
      }, 20);
    }
  };

  // Start game
  const startGame = () => {
    setIsGameRunning(true);
    setGameOver(false);
    setScore(0);
    setObstacles([]);
    setPlayerY(0);
    setIsJumping(false);
    setGameSpeed(6);

    // Spawn obstacles with varying difficulty
    obstacleIntervalRef.current = setInterval(() => {
      const obstacleType = Math.random() > 0.7 ? 'tall' : 'normal';
      setObstacles(prev => [...prev, { 
        id: Date.now(), 
        x: 600,
        type: obstacleType,
        height: obstacleType === 'tall' ? 70 : 50
      }]);
    }, 2000);

    // Game loop with progressive difficulty
    let currentScore = 0;
    let currentSpeed = 6;
    gameLoopRef.current = setInterval(() => {
      currentScore += 1;
      setScore(currentScore);

      // Increase speed gradually
      if (currentScore % 100 === 0) {
        currentSpeed = Math.min(currentSpeed + 0.5, 12);
        setGameSpeed(currentSpeed);
      }

      setObstacles(prev => {
        const updated = prev.map(obs => ({ ...obs, x: obs.x - currentSpeed }))
          .filter(obs => obs.x > -50);
        
        // Improved collision detection
         // More forgiving collision detection
        updated.forEach(obs => {
          // Smaller collision box for more forgiving gameplay
          if (obs.x < 75 && obs.x > 35) {
            setPlayerY(current => {
              // Reduced height thresholds and added buffer
              const threshold = obs.type === 'tall' ? 55 : 35;
              if (current < threshold) {
                endGame(currentScore);
              }
              return current;
            });
          }
        });

        return updated;
      });
    }, 30);
  };

  // End game
  const endGame = (finalScore) => {
    setGameOver(true);
    setIsGameRunning(false);
    if (finalScore > highScore) {
      setHighScore(finalScore);
    }
    clearInterval(gameLoopRef.current);
    clearInterval(obstacleIntervalRef.current);
  };

  // Keyboard controls
  useEffect(() => {
    const handleKeyPress = (e) => {
      if ((e.code === "Space" || e.code === "ArrowUp") && showGame) {
        e.preventDefault();
        if (!isGameRunning) {
          startGame();
        } else {
          jump();
        }
      }
    };
    window.addEventListener("keydown", handleKeyPress);
    return () => window.removeEventListener("keydown", handleKeyPress);
  }, [showGame, isGameRunning, isJumping, gameOver]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (gameLoopRef.current) clearInterval(gameLoopRef.current);
      if (obstacleIntervalRef.current) clearInterval(obstacleIntervalRef.current);
    };
  }, []);

  const handleLoginClick = () => {
    // Replace with your actual navigation logic
    window.location.href = '/login';
  };

  return (
    <div
      style={{
        background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
        minHeight: "100vh",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        color: "#ffffff",
        fontFamily: "'Inter', 'Segoe UI', sans-serif",
        textAlign: "center",
        padding: "20px",
        position: "relative",
        overflow: "hidden",
      }}
    >
      {/* Decorative background elements */}
      <div style={{
        position: "absolute",
        top: "-100px",
        right: "-100px",
        width: "300px",
        height: "300px",
        background: "rgba(255, 255, 255, 0.05)",
        borderRadius: "50%",
        filter: "blur(60px)",
      }} />
      <div style={{
        position: "absolute",
        bottom: "-150px",
        left: "-150px",
        width: "400px",
        height: "400px",
        background: "rgba(255, 255, 255, 0.05)",
        borderRadius: "50%",
        filter: "blur(80px)",
      }} />

      <div style={{
        position: "relative",
        zIndex: 1,
        maxWidth: "900px",
        width: "100%",
      }}>
        {/* Error Icon */}
      <div style={{
  width: "120px",
  height: "120px",
  margin: "0 auto 30px",
  background: "linear-gradient(135deg, rgba(255, 68, 68, 0.2) 0%, rgba(255, 107, 107, 0.15) 100%)",
  borderRadius: "50%",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  backdropFilter: "blur(20px)",
  border: "3px solid rgba(255, 255, 255, 0.3)",
  boxShadow: "0 8px 32px rgba(255, 68, 68, 0.2), inset 0 0 20px rgba(255, 255, 255, 0.1)",
  position: "relative",
  animation: "pulse 2s ease-in-out infinite",
}}>
  <svg 
    width="60" 
    height="60" 
    viewBox="0 0 24 24" 
    fill="none" 
    xmlns="http://www.w3.org/2000/svg"
    style={{
      filter: "drop-shadow(0 2px 8px rgba(255, 255, 255, 0.3))",
    }}
  >
    <path 
      d="M12 2L2 20h20L12 2z" 
      fill="white" 
      opacity="0.95"
    />
    <path 
      d="M12 9v5M12 16h.01" 
      stroke="#ff4444" 
      strokeWidth="2.5" 
      strokeLinecap="round"
    />
  </svg>
  <style dangerouslySetInnerHTML={{__html: `
    @keyframes pulse {
      0%, 100% { transform: scale(1); }
      50% { transform: scale(1.05); }
    }
  `}} />
</div>


        <h1 style={{ 
          fontSize: "2.5rem", 
          marginBottom: "10px",
          fontWeight: "700",
          letterSpacing: "-0.5px",
        }}>
          Authentication Error
        </h1>
        
        <p style={{
          fontSize: "1.1rem",
          marginBottom: "30px",
          opacity: "0.9",
          fontWeight: "400",
        }}>
          We encountered an issue while processing your login request. Please try again!
        </p>

        {/* Quote Card */}
        <div
          style={{
            background: "rgba(255, 255, 255, 0.15)",
            borderRadius: "20px",
            padding: "30px",
            backdropFilter: "blur(20px)",
            border: "1px solid rgba(255, 255, 255, 0.2)",
            marginBottom: "40px",
            boxShadow: "0 8px 32px rgba(0, 0, 0, 0.1)",
          }}
        >
          <div style={{ fontSize: "2.5rem", marginBottom: "15px" }}>
            {quote.emoji}
          </div>
          <p style={{
            fontSize: "1.2rem",
            margin: "0",
            fontWeight: "500",
            lineHeight: "1.6",
          }}>
            "{quote.text}"
          </p>
        </div>

        {!showGame ? (
          <div style={{ 
            display: "flex", 
            gap: "20px", 
            flexWrap: "wrap", 
            justifyContent: "center",
            marginTop: "30px",
          }}>
            <button
              onClick={handleLoginClick}
              style={{
                background: "rgba(255, 255, 255, 0.95)",
                color: "#667eea",
                border: "none",
                borderRadius: "12px",
                padding: "16px 40px",
                fontSize: "1rem",
                fontWeight: "600",
                cursor: "pointer",
                boxShadow: "0 4px 20px rgba(0, 0, 0, 0.15)",
                transition: "all 0.3s ease",
                display: "flex",
                alignItems: "center",
                gap: "10px",
              }}
              onMouseOver={(e) => {
                e.target.style.transform = "translateY(-2px)";
                e.target.style.boxShadow = "0 6px 25px rgba(0, 0, 0, 0.2)";
              }}
              onMouseOut={(e) => {
                e.target.style.transform = "translateY(0)";
                e.target.style.boxShadow = "0 4px 20px rgba(0, 0, 0, 0.15)";
              }}
            >
              <span>🔐</span> Return to Login
            </button>

            <button
              onClick={() => setShowGame(true)}
              style={{
                background: "rgba(255, 255, 255, 0.2)",
                color: "white",
                border: "2px solid rgba(255, 255, 255, 0.3)",
                borderRadius: "12px",
                padding: "16px 40px",
                fontSize: "1rem",
                fontWeight: "600",
                cursor: "pointer",
                backdropFilter: "blur(10px)",
                transition: "all 0.3s ease",
                display: "flex",
                alignItems: "center",
                gap: "10px",
              }}
              onMouseOver={(e) => {
                e.target.style.background = "rgba(255, 255, 255, 0.25)";
                e.target.style.transform = "translateY(-2px)";
              }}
              onMouseOut={(e) => {
                e.target.style.background = "rgba(255, 255, 255, 0.2)";
                e.target.style.transform = "translateY(0)";
              }}
            >
              <span>🎮</span> Play Mini Game
            </button>
          </div>
        ) : (
          <div
            style={{
              background: "rgba(255, 255, 255, 0.95)",
              borderRadius: "24px",
              padding: "30px",
              boxShadow: "0 20px 60px rgba(0, 0, 0, 0.3)",
              maxWidth: "700px",
              width: "100%",
              margin: "0 auto",
            }}
          >
            {/* Game Header */}
            <div style={{ 
              marginBottom: "20px", 
              display: "flex", 
              justifyContent: "space-between", 
              alignItems: "center",
              color: "#667eea",
            }}>
              <div style={{
                background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                padding: "10px 20px",
                borderRadius: "12px",
                color: "white",
                fontWeight: "700",
                fontSize: "1.1rem",
              }}>
                Score: {score}
              </div>
              <div style={{
                padding: "10px 20px",
                borderRadius: "12px",
                background: "rgba(102, 126, 234, 0.1)",
                color: "#667eea",
                fontWeight: "600",
                fontSize: "1rem",
              }}>
                🏆 Best: {highScore}
              </div>
            </div>

            {/* Game Canvas */}
            <div
              onClick={jump}
              style={{
                position: "relative",
                width: "100%",
                height: "250px",
                background: "linear-gradient(to bottom, #87CEEB 0%, #B0E0E6 40%, #F4E4C1 40%, #E8D4A8 100%)",
                borderRadius: "16px",
                overflow: "hidden",
                cursor: "pointer",
                border: "3px solid #667eea",
                boxShadow: "inset 0 2px 10px rgba(0, 0, 0, 0.1)",
              }}
            >
              {/* Clouds */}
              <div style={{
                position: "absolute",
                top: "20px",
                left: `${(score * 2) % 600}px`,
                fontSize: "2rem",
                opacity: "0.6",
              }}>☁️</div>
              <div style={{
                position: "absolute",
                top: "50px",
                left: `${(score * 3) % 600}px`,
                fontSize: "1.5rem",
                opacity: "0.5",
              }}>☁️</div>

              {/* Ground line */}
              <div style={{
                position: "absolute",
                bottom: "0",
                width: "100%",
                height: "3px",
                background: "#8B7355",
              }} />

              {/* Player */}
              <div
                style={{
                  position: "absolute",
                  bottom: `${playerY}px`,
                  left: "60px",
                  width: "45px",
                  height: "45px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  transition: "none",
                  filter: isJumping ? "drop-shadow(0 4px 8px rgba(102, 126, 234, 0.4))" : "none",
                }}
              >
                <div style={{ 
                  fontSize: "40px", 
                  transform: isJumping ? "rotate(-10deg)" : "rotate(0deg)",
                  transition: "transform 0.2s ease",
                }}>⚽</div>
              </div>

              {/* Obstacles */}
              {obstacles.map(obs => (
                <div
                  key={obs.id}
                  style={{
                    position: "absolute",
                    bottom: "0px",
                    left: `${obs.x}px`,
                    width: "35px",
                    height: `${obs.height}px`,
                    display: "flex",
                    alignItems: "flex-end",
                    justifyContent: "center",
                  }}
                >
                  <div style={{ 
                    fontSize: obs.type === 'tall' ? "35px" : "30px",
                    filter: "drop-shadow(2px 2px 4px rgba(0, 0, 0, 0.2))",
                  }}>
                    {obs.type === 'tall' ? '🌴' : '🌵'}
                  </div>
                </div>
              ))}

              {/* Game Over Overlay */}
              {gameOver && (
                <div
                  style={{
                    position: "absolute",
                    top: "0",
                    left: "0",
                    right: "0",
                    bottom: "0",
                    background: "rgba(102, 126, 234, 0.95)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    backdropFilter: "blur(5px)",
                  }}
                >
                  <div style={{
                    background: "white",
                    padding: "30px",
                    borderRadius: "20px",
                    boxShadow: "0 10px 40px rgba(0, 0, 0, 0.3)",
                    textAlign: "center",
                  }}>
                    <h3 style={{ 
                      margin: "0 0 15px 0", 
                      color: "#667eea",
                      fontSize: "1.8rem",
                      fontWeight: "700",
                    }}>Game Over!</h3>
                    <p style={{ 
                      margin: "0 0 10px 0",
                      color: "#666",
                      fontSize: "1.1rem",
                    }}>
                      Final Score: <strong style={{ color: "#667eea" }}>{score}</strong>
                    </p>
                    {score === highScore && score > 0 && (
                      <p style={{ 
                        margin: "0 0 20px 0",
                        color: "#764ba2",
                        fontWeight: "600",
                      }}>
                        🎉 New High Score!
                      </p>
                    )}
                    <button
                      onClick={startGame}
                      style={{
                        background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                        color: "white",
                        border: "none",
                        borderRadius: "12px",
                        padding: "12px 30px",
                        cursor: "pointer",
                        fontSize: "1rem",
                        fontWeight: "600",
                        boxShadow: "0 4px 15px rgba(102, 126, 234, 0.4)",
                        transition: "transform 0.2s ease",
                      }}
                      onMouseOver={(e) => e.target.style.transform = "scale(1.05)"}
                      onMouseOut={(e) => e.target.style.transform = "scale(1)"}
                    >
                      🔄 Play Again
                    </button>
                  </div>
                </div>
              )}

              {/* Start Prompt */}
              {!isGameRunning && !gameOver && (
                <div
                  style={{
                    position: "absolute",
                    top: "50%",
                    left: "50%",
                    transform: "translate(-50%, -50%)",
                    background: "rgba(255, 255, 255, 0.95)",
                    padding: "25px 35px",
                    borderRadius: "16px",
                    boxShadow: "0 8px 30px rgba(0, 0, 0, 0.2)",
                    textAlign: "center",
                  }}
                >
                  <p style={{ 
                    margin: "0 0 10px 0", 
                    fontWeight: "700",
                    color: "#667eea",
                    fontSize: "1.1rem",
                  }}>
                    Ready to Play?
                  </p>
                  <p style={{ 
                    margin: "0",
                    color: "#666",
                    fontSize: "0.9rem",
                  }}>
                    Press SPACE, ↑ or Click to Jump
                  </p>
                </div>
              )}
            </div>

            {/* Game Instructions */}
            <div style={{ 
              marginTop: "20px", 
              padding: "15px",
              background: "rgba(102, 126, 234, 0.1)",
              borderRadius: "12px",
              color: "#667eea",
            }}>
              <p style={{ margin: "0 0 5px 0", fontSize: "0.95rem", fontWeight: "600" }}>
                ⚽ Help the ball avoid obstacles!
              </p>
              <p style={{ margin: "0", fontSize: "0.85rem", opacity: "0.8" }}>
                Game speed increases as you score more points
              </p>
            </div>

            {/* Back Button */}
            <button
              onClick={() => {
                setShowGame(false);
                setIsGameRunning(false);
                setGameOver(false);
                if (gameLoopRef.current) clearInterval(gameLoopRef.current);
                if (obstacleIntervalRef.current) clearInterval(obstacleIntervalRef.current);
              }}
              style={{
                marginTop: "20px",
                background: "transparent",
                color: "#667eea",
                border: "2px solid #667eea",
                borderRadius: "12px",
                padding: "12px 30px",
                fontSize: "0.95rem",
                fontWeight: "600",
                cursor: "pointer",
                transition: "all 0.3s ease",
              }}
              onMouseOver={(e) => {
                e.target.style.background = "#667eea";
                e.target.style.color = "white";
              }}
              onMouseOut={(e) => {
                e.target.style.background = "transparent";
                e.target.style.color = "#667eea";
              }}
            >
              ← Back to Error Page
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default ErrorPage;